import { DEFAULT_ANNOTATION_STYLE } from '@/utils/cad-sheet';

const EXPORT_SIZE = 1800;

/**
 * Render a single image view with all annotations to a canvas and return as data URL.
 * @param {Object} img - image object with url, label, annotations, textBoxes, highlights
 * @param {Object} metadata - optional metadata to overlay: { partyDesignNo, styleNo, pieceName }
 */
export async function renderViewToDataURL(img, metadata = null) {
  const canvas = document.createElement('canvas');
  canvas.width = EXPORT_SIZE;
  canvas.height = EXPORT_SIZE;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, EXPORT_SIZE, EXPORT_SIZE);

  if (img.url) {
    const image = await loadImage(img.url);
    const { dx, dy, dw, dh } = containFit(image.naturalWidth, image.naturalHeight, EXPORT_SIZE, EXPORT_SIZE);
    ctx.drawImage(image, dx, dy, dw, dh);
  }

  drawHighlights(ctx, img.highlights || []);
  drawAnnotations(ctx, img.annotations);
  drawTextBoxes(ctx, img.textBoxes || []);

  // Bottom label bar
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.fillRect(0, EXPORT_SIZE - 60, EXPORT_SIZE, 60);
  ctx.fillStyle = '#333333';
  ctx.font = 'bold 30px Arial, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(img.label, EXPORT_SIZE / 2, EXPORT_SIZE - 22);

  // Metadata overlay (top-left)
  if (metadata) {
    const lines = [];
    if (metadata.partyDesignNo) lines.push(`Design: ${metadata.partyDesignNo}`);
    if (metadata.styleNo) lines.push(`Style: ${metadata.styleNo}`);
    if (metadata.pieceName) lines.push(`Piece: ${metadata.pieceName}`);
    lines.push(`View: ${img.label}`);
    
    const fontSize = 22;
    const lineHeight = fontSize + 8;
    const padding = 12;
    const boxHeight = lines.length * lineHeight + padding * 2;
    
    ctx.font = `bold ${fontSize}px Arial, sans-serif`;
    const maxWidth = Math.max(...lines.map(l => ctx.measureText(l).width));
    
    ctx.fillStyle = 'rgba(0,0,0,0.7)';
    ctx.fillRect(0, 0, maxWidth + padding * 3, boxHeight);
    
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'left';
    lines.forEach((line, i) => {
      ctx.fillText(line, padding, padding + fontSize + i * lineHeight);
    });
  }

  return canvas.toDataURL('image/png');
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (!src.startsWith('blob:') && !src.startsWith('data:')) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = src;
  });
}

function containFit(iw, ih, cw, ch) {
  const scale = Math.min(cw / iw, ch / ih);
  const dw = iw * scale;
  const dh = ih * scale;
  const dx = (cw - dw) / 2;
  const dy = (ch - dh) / 2;
  return { dx, dy, dw, dh };
}

function drawHighlights(ctx, highlights) {
  for (const hl of highlights) {
    if (hl.points.length < 2) continue;
    ctx.save();
    ctx.globalAlpha = hl.opacity;
    ctx.strokeStyle = hl.color;
    ctx.lineWidth = (hl.strokeWidth / 100) * EXPORT_SIZE;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo((hl.points[0].x / 100) * EXPORT_SIZE, (hl.points[0].y / 100) * EXPORT_SIZE);
    for (let i = 1; i < hl.points.length; i++) {
      ctx.lineTo((hl.points[i].x / 100) * EXPORT_SIZE, (hl.points[i].y / 100) * EXPORT_SIZE);
    }
    ctx.stroke();
    ctx.restore();
  }
}

function drawAnnotations(ctx, annotations) {
  const scale = EXPORT_SIZE / 100;
  for (const ann of annotations) {
    const style = ann.style || DEFAULT_ANNOTATION_STYLE;
    const dg = ann.dimensionGeometry;

    ctx.save();
    ctx.strokeStyle = style.lineColor;

    if (dg) {
      ctx.lineWidth = 2.5;
      ctx.setLineDash([8, 5]);
      ctx.globalAlpha = 0.8;
      ctx.beginPath();
      ctx.moveTo(dg.ext1Start.x * scale, dg.ext1Start.y * scale);
      ctx.lineTo(dg.ext1End.x * scale, dg.ext1End.y * scale);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(dg.ext2Start.x * scale, dg.ext2Start.y * scale);
      ctx.lineTo(dg.ext2End.x * scale, dg.ext2End.y * scale);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;

      ctx.lineWidth = style.lineWidth * scale * 0.8;
      ctx.beginPath();
      ctx.moveTo(dg.dimStart.x * scale, dg.dimStart.y * scale);
      ctx.lineTo(dg.dimEnd.x * scale, dg.dimEnd.y * scale);
      ctx.stroke();

      const ddx = dg.dimEnd.x - dg.dimStart.x;
      const ddy = dg.dimEnd.y - dg.dimStart.y;
      const dlen = Math.sqrt(ddx * ddx + ddy * ddy);
      if (dlen > 0.5) {
        const tnx = (-ddy / dlen) * 2.0 * scale;
        const tny = (ddx / dlen) * 2.0 * scale;
        ctx.lineWidth = style.lineWidth * scale * 0.6;
        ctx.beginPath();
        ctx.moveTo(dg.dimStart.x * scale - tnx, dg.dimStart.y * scale - tny);
        ctx.lineTo(dg.dimStart.x * scale + tnx, dg.dimStart.y * scale + tny);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(dg.dimEnd.x * scale - tnx, dg.dimEnd.y * scale - tny);
        ctx.lineTo(dg.dimEnd.x * scale + tnx, dg.dimEnd.y * scale + tny);
        ctx.stroke();
      }
    } else {
      const x1 = ann.x1 * scale;
      const y1 = ann.y1 * scale;
      const x2 = ann.x2 * scale;
      const y2 = ann.y2 * scale;

      ctx.lineWidth = style.lineWidth * scale * 0.8;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();

      drawArrowhead(ctx, x2, y2, x1, y1, style.lineColor, style.lineWidth * scale);
      drawArrowhead(ctx, x1, y1, x2, y2, style.lineColor, style.lineWidth * scale);
    }

    const textX = (ann.textOffsetX !== undefined) ? (ann.x1 + ann.x2) / 2 + ann.textOffsetX : (ann.x1 + ann.x2) / 2;
    const textY = (ann.textOffsetY !== undefined) ? (ann.y1 + ann.y2) / 2 + ann.textOffsetY : (ann.y1 + ann.y2) / 2 - 1.5;

    const refX = dg ? ((dg.dimStart.x + dg.dimEnd.x) / 2) * scale : ((ann.x1 + ann.x2) / 2) * scale;
    const refY = dg ? ((dg.dimStart.y + dg.dimEnd.y) / 2) * scale : ((ann.y1 + ann.y2) / 2) * scale;
    const tX = textX * scale;
    const tY = textY * scale;
    if (Math.abs(tX - refX) > 5 || Math.abs(tY - refY) > 5) {
      ctx.strokeStyle = style.lineColor;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(refX, refY);
      ctx.lineTo(tX, tY);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    ctx.fillStyle = style.textColor;
    ctx.font = `bold ${style.fontSize * scale * 0.9}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const metrics = ctx.measureText(ann.value);
    const pad = 4;
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.fillRect(tX - metrics.width / 2 - pad, tY - style.fontSize * scale * 0.45 - pad, metrics.width + pad * 2, style.fontSize * scale * 0.9 + pad * 2);

    ctx.fillStyle = style.textColor;
    ctx.fillText(ann.value, tX, tY);
    ctx.restore();
  }
}

function drawArrowhead(ctx, fromX, fromY, toX, toY, color, size) {
  const angle = Math.atan2(toY - fromY, toX - fromX);
  const headLen = Math.max(size * 3, 12);
  ctx.save();
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(fromX, fromY);
  ctx.lineTo(fromX + headLen * Math.cos(angle + Math.PI / 6), fromY + headLen * Math.sin(angle + Math.PI / 6));
  ctx.lineTo(fromX + headLen * Math.cos(angle - Math.PI / 6), fromY + headLen * Math.sin(angle - Math.PI / 6));
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

function drawTextBoxes(ctx, textBoxes) {
  const scale = EXPORT_SIZE / 100;
  for (const tb of textBoxes) {
    const style = tb.style || { bold: true, fontSize: 2.8, color: '#000000' };
    const x = tb.x * scale;
    const y = tb.y * scale;

    ctx.save();
    ctx.fillStyle = style.color;
    ctx.font = `${style.bold ? 'bold ' : ''}${style.fontSize * scale * 0.9}px Arial, sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    const metrics = ctx.measureText(tb.value);
    ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.fillRect(x - 2, y - 2, metrics.width + 4, style.fontSize * scale * 0.9 + 4);

    ctx.fillStyle = style.color;
    ctx.fillText(tb.value, x, y);
    ctx.restore();
  }
}
